import React, { useMemo, useState } from 'react';
import { View, Text, Pressable, TextInput } from '../lib/react-native-compat';

// JDE → VoltaGrid labor classification map for the Billing tab's Rates & Rules
// view. JDE payroll stamps every labor cost row with a Labor_Classification
// (Craftsman, Skilled Labor, Foreman (Hourly), …) that doesn't line up with
// the classifications on the VoltaGrid rate sheet, so this card maps each JDE
// classification seen on the job's billable labor cost to a rate-sheet
// classification. Unmapped rows get an auto-suggested match (exact name after
// normalization, then the alias table below); a saved mapping always wins.
//
// Storage (same Forecast_User_Data tab 'Billing' rows as the rate schedule):
//   fieldName 'ratemap' rowId 'rm_<hash(normalized jde name)>'
//     Value = the VoltaGrid classification name ('' = explicitly unmapped),
//     Notes = { j: <jde classification name> }.
// Rendering and save/dirty state live in billingRates.js — this module owns
// the matching logic and the card UI.

// djb2 — same hash billingRateSchedule.js uses for default row ids.
const hash = (s) => {
    let h = 5381;
    for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    return h.toString(36);
};

// Fold a classification name for matching: lowercase, drop the payroll noise
// suffixes JDE appends ((Hourly), (Salary), (PKT), (H), (S)), and collapse
// everything non-alphanumeric so "Welder - Certified (w/o rig)" and
// "Welder - Certified (w/o Rig)" meet in the middle.
export const normClass = (s) => String(s || '')
    .toLowerCase()
    .replace(/\((?:hourly|salary|pkt|h|s)\)/g, ' ')
    .replace(/\bpkt\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s{2,}/g, ' ');

export const mapRowId = (jdeName) => 'rm_' + hash(normClass(jdeName));

// The field's own mapping, from the newest "Multi Phase Field Ticket"
// workbook's Staff List (readFieldTicketMap rows: { id, name, title,
// tier4 }): normalized staff title → the Tier 4 classification the field
// bills that title as, majority vote across people. Slots between a saved
// mapping and the alias-table guess — the field sheet is fresher truth than
// the aliases but a deliberate save still wins.
export const fieldSuggestions = (rows) => {
    const votes = {};
    (rows || []).forEach((r) => {
        const t = normClass(r.title);
        const v = String(r.tier4 || '').trim();
        if (!t || !v) return;
        votes[t] = votes[t] || {};
        votes[t][v] = (votes[t][v] || 0) + 1;
    });
    const out = {};
    Object.keys(votes).forEach((t) => {
        out[t] = Object.entries(votes[t]).sort((a, b) => b[1] - a[1])[0][0];
    });
    return out;
};

// Alias table: normalized JDE classification → rate-sheet classification, for
// the names that don't match on normalization alone. Suggestions only — every
// row is editable on the card, and a saved mapping overrides these. Keyed off
// the distinct Labor_Classification_Description values in prod cost history.
//
// Entries marked below were checked against VoltaGrid's rate buildup workbook
// ("Rate Buildup - Working File 2026 - Strike - 2026.08.31"), whose labor
// sheets carry the payroll classification each rate sheet row was priced off.
// That mapping runs one payroll class to many sheet rows, so it narrows the
// candidates rather than naming one — but where it names exactly one, it wins.
const ALIASES = {
    craftsman: 'Craftsman - A',
    'skilled labor': 'Skilled Laborer',
    'common labor': 'Common Laborer',
    foreman: 'Foreman - Craft',
    'general foreman': 'Foreman - General',
    'welding foreman': 'Foreman - Welding (w/o Rig)',
    strawboss: 'Foreman - Labor',
    'drill foreman': 'Foreman - Craft',
    'civil foreman': 'Foreman - Craft',
    'fiber foreman': 'Foreman - Craft',
    'tie in foreman': 'Foreman - Craft',
    'yard foreman': 'Foreman - Craft',
    'assembly foreman': 'Foreman - Craft',
    'equipment operator': 'Equipment Operator - A',
    'equipment operator iii crane': 'Crane Operator',
    'craft helper': 'Craft Helper - A',
    welder: 'Welder - Certified (w/o Rig)',
    'welder w o rig': 'Welder - Certified (w/o Rig)',
    'welder in service': 'Welder - In-Service (w/o Rig)',
    'pipe welder': 'Welder - Certified (w/o Rig)',
    'pipe welder iii': 'Welder - Certified (w/o Rig)',
    'structural welder': 'Welder - Certified (w/o Rig)',
    'welder helper': 'Craft Helper - A',
    superintendent: 'Superintendent - General',
    'general superintendent': 'Superintendent - General',
    'truck driver': 'Truck Driver - A (Equipment Operator w/ CDL)',
    'qa qc specialist': 'Quality Control Inspector I',
    'qa specialist iii': 'Quality Control Inspector III',
    'qa qc manager': 'Quality Manager',
    'qa qc lead': 'Quality Control Inspector IV',
    'qa qc weld inspector': 'Quality Control Inspector I',
    'qa qc assistant': 'Quality Control Inspector IV',
    'quality specialist': 'Quality Control Inspector I',
    'hse specialist i': 'HSE Specialist',
    'hse specialist ii': 'HSE Specialist',
    'hse specialist iii': 'HSE Specialist',
    'lead hse specialist': 'HSE Specialist',
    'hse oq specialist i': 'HSE Specialist',
    'hse oq specialist ii': 'HSE Specialist',
    'hse representative ii': 'HSE Specialist',
    'hse manager': 'Safety Manager',
    'hse supervisor oq compliance': 'Safety Manager',
    'sr project manager': 'Senior Project Manager',
    'sr manager estimating': 'Senior Project Manager',
    // Project Controls Specialist II / Lead priced the Planner and Cost
    // Controller rows, never Project Controls Manager. Sr. Planner and Sr.
    // Cost Controller are the same $75 wage (Planner / Cost Controller the
    // same $65), so either side of the pair bills identically.
    'project controls specialist ii': 'Sr. Planner',
    'project controls lead': 'Planner',
    'helper i': 'Mechanic Helper',
    'assistant project manager ii': 'Assistant Project Manager',
    'assistant project manager iii': 'Assistant Project Manager',
    'pipe fitter': 'Craftsman - A',
    carpenter: 'Craftsman - A',
    'lead carpenter': 'Craftsman - A',
    pumper: 'Craftsman - A',
    painter: 'Craftsman - A',
    blaster: 'Craftsman - A',
    'boiler maker': 'Craftsman - A',
    spotter: 'Common Laborer',
    'electrical journeyman i': 'Journeyman Electrician',
    'electrical journeyman ii': 'Journeyman Electrician',
    'electrical foreman': 'I&E Foreman',
    'electrical superintendent': 'I&E Superintendent',
    'ie apprentice 1st year': 'Electrician Apprentice 1st / 2nd year',
    'ie apprentice 2nd year': 'Electrician Apprentice 1st / 2nd year',
    'ie apprentice 3rd year': 'Electrician Apprentice 3rd / 4th year',
    'ie apprentice 4th year': 'Electrician Apprentice 3rd / 4th year',
    'pipe bender': 'Bending Engineer',
    'material coordinator': 'Project Field Materials Coordinator',
    'document controls specialist': 'Document Control Specialist',
    'document control spec': 'Document Control Specialist',
    'document control analyst ii': 'Document Control Specialist',
    'doc controls manager': 'Document Control Manager',
    // JDE's description column truncates at 30 chars.
    'project admin support speciali': 'Project Admin Support Specialist',
    'project coordinator i': 'Project Coordinator',
    'project coordinator iii': 'Project Coordinator',
    'procurement spec ii': 'Procurement Specialist',
    'procurement specialist i': 'Procurement Specialist',
    'procurement specialist iii': 'Procurement Specialist',
    'mechanical engineer': 'Mechanical Project Engineer',
    'electrical engineer': 'Electrical Project Engineer',
    'office manager iii': 'Office Manager',
};

// Character-bigram Dice coefficient, the last-resort similarity score when a
// classification matches nothing by name or alias.
const bigrams = (s) => {
    const out = new Map();
    for (let i = 0; i < s.length - 1; i += 1) {
        const g = s.slice(i, i + 2);
        out.set(g, (out.get(g) || 0) + 1);
    }
    return out;
};

const dice = (a, b) => {
    if (!a || !b) return 0;
    const ga = bigrams(a);
    const gb = bigrams(b);
    let hits = 0;
    let na = 0;
    let nb = 0;
    ga.forEach((n) => { na += n; });
    gb.forEach((n) => { nb += n; });
    ga.forEach((n, g) => { hits += Math.min(n, gb.get(g) || 0); });
    return na + nb ? (2 * hits) / (na + nb) : 0;
};

// Best rate-sheet classification for a JDE classification, restricted to names
// that actually exist on this job's (possibly edited) labor schedule. Exact
// match after normalization wins, then the alias table, then a best guess by
// shared words with character bigrams as the tiebreak — every classification
// lands on a rate, and every row stays editable on the card. billing.js prices
// off this same function, so what the card shows is what the job bills.
// JDE classes that bill as a different sheet line even though a sheet line
// carries their exact name. VoltaGrid's quality manager is the senior one.
const FORCED = {
    'quality manager': 'Sr. Quality Manager',
};

export const suggestVoltaClass = (jdeName, laborNames) => {
    const names = laborNames || [];
    if (!names.length) return '';
    const byNorm = {};
    names.forEach((n) => { const k = normClass(n); if (!byNorm[k]) byNorm[k] = n; });
    const key = normClass(jdeName);
    if (!key) return '';
    // Overrides beat the exact-name match: the JDE class shares a name with a
    // sheet line but the job bills it as another.
    const forced = FORCED[key];
    if (forced && names.includes(forced)) return forced;
    if (byNorm[key]) return byNorm[key];
    const alias = ALIASES[key];
    if (alias && names.includes(alias)) return alias;
    const keyTokens = new Set(key.split(' ').filter(Boolean));
    let best = '';
    let bestScore = -1;
    names.forEach((n) => {
        const nk = normClass(n);
        const toks = nk.split(' ').filter(Boolean);
        const shared = toks.filter((t) => keyTokens.has(t)).length;
        // Score how much of the JDE name the sheet name covers, NOT how much
        // of the sheet name it uses — dividing by the longer of the two would
        // rank 'Piping Superintendent' over 'Welding Superintendent (w/o Rig)'
        // for 'Welding Superintendent', purely for being shorter.
        const overlap = shared / keyTokens.size;
        // Shared words dominate; bigrams only separate otherwise-equal names.
        const score = (overlap * 2) + dice(key, nk);
        if (score > bestScore) { bestScore = score; best = n; }
    });
    return best;
};

// The company-wide JDE classification catalog (readLaborClassifications) comes
// back as [{ Labor_Classification_Description, Job_Group }] — fold it to
// [{ name, group }] and drop blanks/dups so the card can list every
// classification, not just the ones that have hit this job's cost.
export const parseClassCatalog = (rows) => {
    const seen = new Set();
    const out = [];
    (rows || []).forEach((r) => {
        const name = String(r?.Labor_Classification_Description ?? r?.name ?? '').trim();
        if (!name || seen.has(name)) return;
        seen.add(name);
        out.push({ name, group: String(r?.Job_Group ?? r?.group ?? '').trim() });
    });
    return out.sort((a, b) => a.name.localeCompare(b.name));
};

// Stored mappings out of the job's Billing rows: { <jde name>: <volta name> }.
// A row that exists with a blank Value is an explicit "unmapped" (it blocks
// the auto-suggestion); blank Value + blank Notes is the reset no-op.
export const parseLaborMap = (userRows) => {
    const map = {};
    (userRows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== 'ratemap') return;
        if (!String(x.Notes || '').trim() && !String(x.Forecast_Value || '').trim()) return;
        try {
            const p = JSON.parse(x.Notes);
            if (p && p.j) map[p.j] = String(x.Forecast_Value || '');
        } catch (e) { /* malformed blob — ignore */ }
    });
    return map;
};

const th = { fontSize: 10, fontWeight: '700', color: '#64748b', letterSpacing: 0.4, paddingTop: 6, paddingBottom: 6, paddingRight: 8 };

const UNMAPPED = '— Unmapped —';

// Searchable single-select, same overlay pattern as procurementFilters'
// FilterDropdown but picking one value and closing on select. Also used by
// the equipment tier map card (billingEquipMap.js).
export const SelectBox = ({ value, options, onSelect, disabled, suggested }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const q = query.trim().toLowerCase();
    const shown = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
    return (
        <View style={{ position: 'relative', width: 260, zIndex: open ? 30 : 1 }}>
            <Pressable
                onPress={() => { if (!disabled) { setOpen(!open); setQuery(''); } }}
                style={{
                    flexDirection: 'row', alignItems: 'center',
                    paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8,
                    borderRadius: 4, borderWidth: 1,
                    borderColor: open ? '#0f172a' : '#cbd5e1',
                    backgroundColor: disabled ? '#f8fafc' : '#fff',
                }}
            >
                <Text style={{ fontSize: 12, color: value ? '#1e293b' : '#94a3b8', flexGrow: 1 }} numberOfLines={1}>
                    {value || UNMAPPED}
                </Text>
                {suggested ? (
                    <Text style={{ fontSize: 9, fontWeight: '700', color: '#92400e', backgroundColor: '#fef3c7', borderRadius: 3, paddingLeft: 4, paddingRight: 4, marginRight: 4 }}>
                        SUGGESTED
                    </Text>
                ) : null}
                <Text style={{ fontSize: 11, color: '#64748b' }}>{open ? '▴' : '▾'}</Text>
            </Pressable>
            {open ? (
                <>
                    <Pressable
                        onPress={() => { setOpen(false); setQuery(''); }}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, cursor: 'default' }}
                    />
                    <View
                        style={{
                            position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20,
                            backgroundColor: '#fff', borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 6,
                            minWidth: 300,
                            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                            paddingTop: 4, paddingBottom: 4,
                        }}
                    >
                        <View style={{ paddingTop: 4, paddingBottom: 6, paddingLeft: 8, paddingRight: 8, borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                            <TextInput
                                value={query}
                                onChangeText={setQuery}
                                placeholder="Search classifications..."
                                autoFocus
                                style={{
                                    fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
                                    paddingTop: 5, paddingBottom: 5, paddingLeft: 8, paddingRight: 8,
                                    width: '100%',
                                }}
                            />
                        </View>
                        <View style={{ maxHeight: 260, overflowY: 'auto' }}>
                            {[UNMAPPED, ...shown].map((o) => {
                                const val = o === UNMAPPED ? '' : o;
                                const on = val === (value || '');
                                return (
                                    <Pressable
                                        key={o}
                                        onPress={() => { onSelect(val); setOpen(false); setQuery(''); }}
                                        style={{
                                            paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12,
                                            backgroundColor: on ? '#f1f5f9' : 'transparent',
                                        }}
                                    >
                                        <Text style={{ fontSize: 12, color: o === UNMAPPED ? '#94a3b8' : '#1e293b', fontWeight: on ? '700' : '400', whiteSpace: 'nowrap' }}>
                                            {o}
                                        </Text>
                                    </Pressable>
                                );
                            })}
                            {shown.length === 0 ? (
                                <Text style={{ fontSize: 12, color: '#94a3b8', paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12 }}>No matches</Text>
                            ) : null}
                        </View>
                    </View>
                </>
            ) : null}
        </View>
    );
};

const fmtHrs = (n) => (Math.round((Number(n) || 0) * 10) / 10).toLocaleString('en-US');

// The card. Draft state (patch) lives in billingRates.js so mapping edits ride
// the page's single Save-changes flow.
//   jdeClasses: [{ name, hrs }] from the job's billable labor cost
//   catalog:    every active JDE classification company-wide (parseClassCatalog)
//   labor:      the merged (defaults + overrides) labor schedule rows
//   stored:     parseLaborMap(userRows)
//   patch:      unsaved edits { <jde name>: <volta name | ''> }
//   rateOf:     (voltaName) => { st, ot } | null
const LaborClassMap = ({ jdeClasses, catalog, labor, stored, patch, onChange, rateOf, locked, fieldSug }) => {
    const [query, setQuery] = useState('');
    const laborNames = useMemo(() => labor.map((r) => r.name).filter(Boolean), [labor]);
    // Dropdown options only — laborNames itself stays in rate-sheet order for
    // the coverage gap list and the suggestion tie-break.
    const sortedLaborNames = useMemo(() => [...laborNames].sort((a, b) => a.localeCompare(b)), [laborNames]);

    // Job cost classifications, stored mappings for classifications that no
    // longer appear on this job's cost, and the rest of the JDE catalog (so
    // the map can be filled in ahead of the cost) — one list, sorted A–Z to
    // match the exported workbook.
    const { onJob, rest } = useMemo(() => {
        const inJob = new Set((jdeClasses || []).map((c) => c.name));
        const extras = Object.keys(stored || {}).filter((j) => !inJob.has(j))
            .map((name) => ({ name, hrs: 0, extra: true }));
        const seen = new Set([...inJob, ...extras.map((e) => e.name)]);
        const bidNorm = new Set(laborNames.map(normClass));
        return {
            onJob: [...(jdeClasses || []), ...extras].map((r) => ({ ...r, bid: bidNorm.has(normClass(r.name)) })),
            rest: (catalog || []).filter((c) => !seen.has(c.name))
                .map((c) => ({ name: c.name, hrs: 0, extra: true, catalogOnly: true, bid: bidNorm.has(normClass(c.name)) })),
        };
    }, [jdeClasses, stored, catalog, laborNames]);

    // What a row bills as today: an unsaved edit wins, then a saved mapping,
    // then the field workbook's own mapping, then the best guess — the same
    // precedence billing.js prices on.
    const valueFor = (name) => {
        if (patch[name] !== undefined) return patch[name];
        if (stored[name] !== undefined) return stored[name];
        const f = fieldSug && fieldSug[normClass(name)];
        if (f && laborNames.includes(f)) return f;
        return suggestVoltaClass(name, laborNames);
    };

    // Rate sheet classifications nothing maps to — the coverage question this
    // card can finally answer now that the whole JDE catalog is in the table.
    const gaps = useMemo(() => {
        const hit = new Set();
        [...onJob, ...rest].forEach((r) => { const v = valueFor(r.name); if (v) hit.add(v); });
        return laborNames.filter((n) => !hit.has(n));
        // valueFor is derived from patch/stored/fieldSug, all in the dep list
    }, [onJob, rest, laborNames, patch, stored, fieldSug]);

    const q = query.trim().toLowerCase();
    // The whole list is on screen sorted A–Z, so the search box only narrows
    // it. Rows keep their position number from the full list even while
    // filtered, so "row 214 of 317" stays meaningful mid-search.
    const all = useMemo(() => [...onJob, ...rest]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((r, i) => ({ ...r, n: i + 1 })), [onJob, rest]);
    const rows = q ? all.filter((r) => r.name.toLowerCase().includes(q)) : all;

    if (!all.length) {
        return (
            <Text style={{ fontSize: 12, color: '#94a3b8' }}>
                No labor cost with a JDE classification on this job yet — rows appear here as billable labor posts to cost history.
            </Text>
        );
    }

    return (
        <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <TextInput
                    value={query}
                    onChangeText={setQuery}
                    placeholder="Search all JDE classifications..."
                    style={{
                        fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1', borderWidth: 1,
                        paddingTop: 5, paddingBottom: 5, paddingLeft: 8, paddingRight: 8, width: 260,
                    }}
                />
                <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 10 }}>
                    {q ? `${rows.length} of ${all.length} classifications` : `${all.length} classifications · ${onJob.length} with cost on this job`}
                </Text>
            </View>
            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                <Text style={[th, { width: 40 }]}>#</Text>
                <Text style={[th, { flexGrow: 1 }]}>JDE LABOR CLASSIFICATION</Text>
                <Text style={[th, { width: 90, textAlign: 'right' }]}>JTD HOURS</Text>
                <Text style={[th, { width: 268, paddingLeft: 12 }]}>RATE SHEET CLASSIFICATION</Text>
                <Text style={[th, { width: 90, textAlign: 'right' }]}>ST RATE</Text>
                <Text style={[th, { width: 90, textAlign: 'right' }]}>OT RATE</Text>
            </View>
            {rows.map((r) => {
                const value = valueFor(r.name);
                const rate = value ? rateOf(value) : null;
                return (
                    <View key={r.name} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', paddingTop: 3, paddingBottom: 3, backgroundColor: r.bid ? '#eff6ff' : 'transparent' }}>
                        <Text style={{ fontSize: 11, color: '#94a3b8', width: 40 }}>{r.n}</Text>
                        <View style={{ flexGrow: 1, flexBasis: 0, paddingRight: 8 }}>
                            <Text style={{ fontSize: 12, color: r.catalogOnly ? '#475569' : '#1e293b' }}>{r.name}</Text>
                            {r.bid ? (
                                <Text style={{ fontSize: 10, color: '#94a3b8' }}>bid classification</Text>
                            ) : null}
                            {r.extra && !r.catalogOnly ? (
                                <Text style={{ fontSize: 10, color: '#94a3b8' }}>saved mapping — not on this job's cost</Text>
                            ) : null}
                        </View>
                        <Text style={{ fontSize: 12, color: '#64748b', width: 90, textAlign: 'right', paddingRight: 8 }}>
                            {r.extra ? '—' : fmtHrs(r.hrs)}
                        </Text>
                        <View style={{ width: 268, paddingLeft: 12 }}>
                            <SelectBox
                                value={value}
                                options={sortedLaborNames}
                                onSelect={(v) => onChange(r.name, v)}
                                disabled={locked}
                            />
                        </View>
                        <Text style={{ fontSize: 12, color: '#1e293b', width: 90, textAlign: 'right' }}>{rate ? rate.st : '—'}</Text>
                        <Text style={{ fontSize: 12, color: '#1e293b', width: 90, textAlign: 'right' }}>{rate ? rate.ot : '—'}</Text>
                    </View>
                );
            })}
            {rows.length === 0 ? (
                <Text style={{ fontSize: 12, color: '#94a3b8', paddingTop: 8, paddingBottom: 8 }}>{`No classification matches "${query}".`}</Text>
            ) : null}
            <View style={{ marginTop: 8 }}>
                <Text style={{ fontSize: 11, color: '#64748b' }}>
                    {`Rate sheet coverage: ${laborNames.length - gaps.length} of ${laborNames.length} classifications have a JDE classification mapped to them.`}
                </Text>
                {gaps.length ? (
                    <View style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#0f172a' }}>
                            {`${gaps.length} unmapped — nothing on this job bills at these rates:`}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, lineHeight: 16 }}>{gaps.join(' · ')}</Text>
                    </View>
                ) : (
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        Every rate sheet classification has at least one JDE classification mapped to it.
                    </Text>
                )}
            </View>
        </View>
    );
};

export default LaborClassMap;
